import { computed } from 'vue'
import type { Epic, EpicStatus } from '@/types'
import type { EpicsState } from './state'

export const createGetters = (state: EpicsState) => {
  const filteredEpics = computed(() => {
    let result = [...state.epics]

    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      result = result.filter(
        (epic) =>
          epic.title.toLowerCase().includes(search) ||
          epic.key.toLowerCase().includes(search) ||
          epic.description?.toLowerCase().includes(search)
      )
    }

    if (state.filters.projectId) {
      result = result.filter((epic) => epic.projectId === state.filters.projectId)
    }

    if (state.filters.applicationId) {
      result = result.filter((epic) => epic.applicationId === state.filters.applicationId)
    }

    if (state.filters.status) {
      result = result.filter((epic) => epic.status === state.filters.status)
    }

    if (state.filters.priority) {
      result = result.filter((epic) => epic.priority === state.filters.priority)
    }

    if (state.filters.assignedTo) {
      result = result.filter((epic) => epic.assignedTo === state.filters.assignedTo)
    }

    return result
  })

  const epicsByProject = computed(() => {
    const grouped: Record<string, Epic[]> = {}
    state.epics.forEach((epic) => {
      if (!grouped[epic.projectId]) {
        grouped[epic.projectId] = []
      }
      grouped[epic.projectId].push(epic)
    })
    return grouped
  })

  const epicsByStatus = computed(() => {
    const grouped: Record<EpicStatus, Epic[]> = {
      draft: [],
      ready: [],
      in_progress: [],
      completed: [],
      cancelled: [],
    }
    state.epics.forEach((epic) => {
      grouped[epic.status].push(epic)
    })
    return grouped
  })

  const draftEpics = computed(() => state.epics.filter((e) => e.status === 'draft'))
  const readyEpics = computed(() => state.epics.filter((e) => e.status === 'ready'))
  const inProgressEpics = computed(() => state.epics.filter((e) => e.status === 'in_progress'))
  const completedEpics = computed(() => state.epics.filter((e) => e.status === 'completed'))

  const getEpicById = (id: string) => computed(() => state.epics.find((e) => e.id === id))

  const getEpicsByProjectId = (projectId: string) =>
    computed(() => state.epics.filter((e) => e.projectId === projectId))

  const getEpicsByApplicationId = (applicationId: string) =>
    computed(() => state.epics.filter((e) => e.applicationId === applicationId))

  return {
    filteredEpics,
    epicsByProject,
    epicsByStatus,
    draftEpics,
    readyEpics,
    inProgressEpics,
    completedEpics,
    getEpicById,
    getEpicsByProjectId,
    getEpicsByApplicationId,
  }
}
